<script setup lang="ts">
/**
 * SWIPE DECK — one item grid, two behaviours by screen size.
 *
 *  • Desktop (>640px): a plain auto-fill grid — the page wrappers collapse via
 *    `display:contents`, so items flow exactly like a normal grid. Pass the
 *    column min-width / gap so it matches the host's look.
 *  • Mobile (≤640px): a 2-column, `pageSize`-per-page horizontal carousel with
 *    CSS scroll-snap — the player swipes left/right between pages (no page ever
 *    scrolls sideways; each page is exactly the deck's width). Dots + ‹ › below.
 *
 * The item markup is provided by the default scoped slot, so the host keeps a
 * single copy of its tile. Trailing empty cells (`padClass`) fill the desktop
 * grid up to `minCells` and top the last mobile page up to a full row.
 *
 * `rowStep` (opt-in, off by default) caps the DESKTOP grid to N rows with a
 * "load more" that reveals N more. It is desktop-only on purpose: the mobile
 * carousel is already paginated by swiping, so capping there would page the
 * pages. Because the columns are `auto-fill`, a row is not a fixed number of
 * items — the count is measured from the live grid, so "5 rows" stays 5 rows at
 * every width instead of guessing an item count that is wrong on most screens.
 */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { t } from '../i18n';

const props = withDefaults(defineProps<{
  items: readonly unknown[];
  pageSize?: number;      // items per mobile page (2 cols × N rows)
  minCol?: string;        // desktop grid column min-width, e.g. '150px'
  gap?: string;           // desktop grid gap, e.g. '0.75rem'
  minCells?: number;      // desktop: pad with empty cells up to this many
  padPage?: boolean;      // mobile: pad the last page up to a full pageSize
  padClass?: string;      // class applied to the empty pad cells
  itemKey?: (item: unknown, index: number) => string | number;
  rowStep?: number;       // desktop: show N rows, then "load more" N at a time (0 = off)
  mobileCols?: number;    // mobile: columns per carousel page (2 = the tray's look)
  /**
   * Desktop: use `auto-fit` instead of `auto-fill`. With auto-FILL a short list
   * on a wide screen leaves empty tracks and the cards stay minCol-narrow; with
   * auto-FIT the empty tracks collapse and the cards stretch to fill the row.
   * Off by default so existing callers keep the grid they were built against —
   * the shrine, which shows three big cards, wants it on.
   */
  autoFit?: boolean;
}>(), {
  pageSize: 6,
  minCol: '150px',
  gap: '0.75rem',
  minCells: 0,
  padPage: false,
  padClass: '',
  rowStep: 0,
  mobileCols: 2,
  autoFit: false,
});

const deck = ref<HTMLElement | null>(null);
const current = ref(0);

/**
 * Resolved during setup, NOT in onMounted. Defaulting to false and correcting
 * after mount meant a phone's first paint rendered the desktop branch — with
 * `rowStep` on, that is a visible flash of a capped grid plus a "load more"
 * button before both disappear. `matchMedia` is guarded for non-browser runs.
 */
const isMobile = ref(
  typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 640px)').matches,
);
let mql: MediaQueryList | null = null;
function syncMobile() { isMobile.value = !!mql?.matches; }

// ── Row cap (desktop only, opt-in via rowStep) ──
/** Rows currently revealed. Grows by rowStep on each "load more". */
const shownRows = ref(props.rowStep);
/** Live column count, read off the resolved grid — see the header comment. */
const cols = ref(1);
const capped = computed(() => props.rowStep > 0 && !isMobile.value);

function measureCols() {
  if (!deck.value) return;
  // On mobile the deck is a flex carousel, so there is no template to read;
  // `capped` is false there anyway and the fallback of 1 is never used.
  const tpl = getComputedStyle(deck.value).gridTemplateColumns;
  const n = tpl && tpl !== 'none' ? tpl.split(' ').filter(Boolean).length : 0;
  cols.value = Math.max(1, n);
}

const visibleItems = computed(() => (capped.value
  ? props.items.slice(0, cols.value * shownRows.value)
  : props.items));

const remaining = computed(() => Math.max(0, props.items.length - visibleItems.value.length));

function loadMore() {
  shownRows.value += props.rowStep;
}

const pages = computed(() => {
  const src = visibleItems.value;
  const out: unknown[][] = [];
  for (let i = 0; i < src.length; i += props.pageSize) {
    out.push(src.slice(i, i + props.pageSize) as unknown[]);
  }
  return out;
});

// Empty cells trailing the LAST page: desktop fills to minCells, mobile tops the
// final page to a full pageSize. On desktop `display:contents` lets them flow
// into the grid as ordinary trailing cells.
const padCount = computed(() => {
  // Counts what is actually rendered, so a capped grid is not padded out to
  // minCells with empty cells that suggest the list has ended.
  const n = visibleItems.value.length;
  if (isMobile.value) {
    if (!props.padPage || n === 0) return 0;
    return (props.pageSize - (n % props.pageSize)) % props.pageSize;
  }
  return Math.max(0, props.minCells - n);
});

function keyOf(item: unknown, index: number) {
  if (props.itemKey) return props.itemKey(item, index);
  const id = (item as { id?: string | number } | null)?.id;
  return id ?? index;
}

function onScroll() {
  if (!deck.value) return;
  const w = deck.value.clientWidth || 1;
  current.value = Math.round(deck.value.scrollLeft / w);
}
function goTo(i: number) {
  if (!deck.value) return;
  const clamped = Math.max(0, Math.min(pages.value.length - 1, i));
  deck.value.scrollTo({ left: clamped * deck.value.clientWidth, behavior: 'smooth' });
}

// Reset to the first page whenever the item set changes (e.g. filter switch).
// The row cap collapses back to the first step too — switching filter should
// show the top of the new set, not however deep the player had loaded the old one.
watch(() => props.items, async () => {
  current.value = 0;
  shownRows.value = props.rowStep;
  await nextTick();
  if (deck.value) deck.value.scrollLeft = 0;
  measureCols();
});

let ro: ResizeObserver | null = null;

onMounted(() => {
  mql = window.matchMedia('(max-width: 640px)');
  syncMobile();
  mql.addEventListener?.('change', syncMobile);

  if (props.rowStep > 0) {
    measureCols();
    // The column count is width-derived, so it has to be re-read on resize —
    // otherwise "5 rows" silently becomes 3 or 8 as the window changes.
    if (typeof ResizeObserver !== 'undefined' && deck.value) {
      ro = new ResizeObserver(measureCols);
      ro.observe(deck.value);
    }
  }
});
onUnmounted(() => {
  mql?.removeEventListener?.('change', syncMobile);
  ro?.disconnect();
});
</script>

<template>
  <div class="deck-wrap">
    <div
      ref="deck" class="deck" :class="{ 'deck--fit': autoFit }"
      :style="{ '--deck-min': minCol, '--deck-gap': gap, '--deck-mcols': mobileCols }"
      @scroll.passive="onScroll"
    >
      <div v-for="(pg, pi) in pages" :key="pi" class="deck-page">
        <slot
          v-for="(it, ii) in pg" :key="keyOf(it, pi * pageSize + ii)"
          :item="it" :index="pi * pageSize + ii"
        />
        <template v-if="pi === pages.length - 1 && padCount > 0">
          <div v-for="n in padCount" :key="'pad-' + n" class="deck-pad" :class="padClass"></div>
        </template>
      </div>
    </div>

    <!-- desktop row cap: reveal another rowStep rows. Hidden on mobile by CSS,
         where the carousel already pages, so the two controls never both show. -->
    <div v-if="capped && remaining > 0" class="deck-more">
      <button class="btn-forge btn-forge--carved btn-forge--sm" @click="loadMore">
        {{ t('common.loadMore') }}
      </button>
      <span class="deck-more-left">{{ t('common.itemsLeft', { count: remaining }) }}</span>
    </div>

    <!-- mobile-only pager: ‹ 3 / 18 › with dots when the count is small -->
    <div v-if="pages.length > 1" class="deck-nav" aria-hidden="false">
      <button class="deck-arrow" :disabled="current === 0" @click="goTo(current - 1)" :aria-label="t('common.prevPage')">‹</button>
      <div v-if="pages.length <= 10" class="deck-dots">
        <button
          v-for="(pg, pi) in pages" :key="'dot-' + pi"
          class="deck-dot" :class="{ 'deck-dot-on': pi === current }"
          @click="goTo(pi)" :aria-label="t('common.pageN', { page: pi + 1 })"
        ></button>
      </div>
      <span v-else class="deck-count">{{ current + 1 }} / {{ pages.length }}</span>
      <button class="deck-arrow" :disabled="current === pages.length - 1" @click="goTo(current + 1)" :aria-label="t('common.nextPage')">›</button>
    </div>
  </div>
</template>

<style scoped>
.deck-wrap { min-width: 0; }

/* ── desktop: page wrappers dissolve, items flow in one auto-fill grid ── */
.deck {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--deck-min, 150px), 1fr));
  gap: var(--deck-gap, 0.75rem);
}
/* A separate class rather than a var inside repeat() — `repeat(var(--x), …)` is
   not reliably supported, and this has to be right on every browser. */
.deck--fit {
  grid-template-columns: repeat(auto-fit, minmax(var(--deck-min, 150px), 1fr));
}
.deck-page { display: contents; }
.deck-nav { display: none; }

/* ── the row-cap control. Centred under the grid and deliberately quiet: it is a
   "there is more" marker, not a call to action competing with the Buy buttons in
   the cards above it. ── */
.deck-more {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  margin-top: 16px;
}
.deck-more-left {
  font-family: var(--font-label, sans-serif);
  font-size: 10px; letter-spacing: 0.07em; text-transform: uppercase;
  color: var(--color-text-dim);
}

/* trailing empty cells (desktop min-fill / last mobile page) */
.deck-pad {
  border: 1px dashed var(--color-border, #2a2436);
  border-radius: 0;
  min-height: 100px;
  background: rgba(0, 0, 0, 0.12);
}

/* ── mobile: horizontal scroll-snap carousel, 2 columns per page ── */
@media (max-width: 640px) {
  /* The carousel is the pagination here — a load-more button underneath would be
     a second, contradictory pager. (`capped` is already false at this width; this
     is belt-and-braces for the resize transition.) */
  .deck-more { display: none; }

  .deck {
    display: flex;
    grid-template-columns: none;
    gap: 0;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .deck::-webkit-scrollbar { display: none; }
  .deck-page {
    display: grid;
    /* 2 by default (the tray); 1 for a deck of large hero cards like the shrine.
       minmax(0, …) is load-bearing: a bare `1fr` floors each track at its
       content's min-content width, so one long un-wrappable item name (the
       cards set `white-space: nowrap` and ellipsise) blew the first track to
       171px of a 299px page and pushed the second column 21px past the page
       edge — pages 3-4 of the rack, where the long names live, rendered as a
       clipped right column with the neighbouring page bleeding in. */
    grid-template-columns: repeat(var(--deck-mcols, 2), minmax(0, 1fr));
    grid-auto-rows: max-content;
    gap: 10px;
    flex: 0 0 100%;
    width: 100%;
    scroll-snap-align: start;
    /* one swipe = one page: without this a fling crosses 3-4 snap points at
       once and the rack feels like it lost your place */
    scroll-snap-stop: always;
    padding: 3px 2px;   /* keep card shadows/hover-lift from clipping */
  }

  .deck-nav {
    display: flex; align-items: center; justify-content: center; gap: 12px;
    margin-top: 12px;
  }
  /* 44px on a phone: the carousel's arrows are the pager, and a 30px box is
     under the tap-target floor (the dots next to them buy their 44px with an
     invisible ::before instead, since ten full-size boxes would not fit) */
  .deck-arrow {
    display: grid; place-items: center;
    width: 44px; height: 44px;
    font-family: var(--font-heading, serif); font-size: 18px; line-height: 1;
    color: var(--color-gold);
    border: 1px solid color-mix(in srgb, var(--color-gold) 40%, transparent);
    background: rgba(0, 0, 0, 0.3); border-radius: 0;
  }
  .deck-arrow:disabled { opacity: 0.3; }
  .deck-dots { display: flex; align-items: center; gap: 7px; }
  /* the dot stays 7px (a row of up to 10 full-size 44px targets would not fit
     a phone width) — instead an invisible ::before extends the actual tap
     area to the 44px floor without touching layout: a pseudo-element's
     painted area still resolves clicks to its host, so this is a real hit-box
     enlargement, not just a visual one. */
  .deck-dot {
    position: relative;
    width: 7px; height: 7px; border-radius: 0;
    border: 0; padding: 0;
    background: color-mix(in srgb, var(--color-text-dim) 60%, transparent);
    transition: background 150ms ease, transform 150ms ease;
  }
  .deck-dot::before {
    content: '';
    position: absolute;
    inset: -18.5px; /* (44px - 7px) / 2 */
  }
  .deck-dot-on { background: var(--color-gold); transform: scale(1.35); }
  .deck-count {
    font-family: var(--font-heading, serif); font-size: 13px;
    color: var(--color-text-dim); font-variant-numeric: tabular-nums;
    min-width: 54px; text-align: center;
  }
}
</style>
